/**
 * Mock das telas master Permissões e Custos de IA, do catálogo do OpenRouter
 * (/bff/ai/models) e dos créditos do usuário (/ai-credits/me). Espelha as
 * regras da legacy-api (services/aiSettings.js, services/aiAdmin.js): cascata
 * global > passbook > loja > usuário, reset mensal e consumo por conversa.
 *
 * O consumo de cada usuário é derivado do próprio extrato, então percentuais,
 * KPIs e extrato sempre batem entre si. Mutações persistem na sessão.
 */
import type { Passbook } from '@/shared/types';
import type {
  AiGlobalSettings,
  AiModelOption,
  AiSettingField,
  AiSettings,
  AiSettingSource,
  AiUserRow,
  EffectiveAiSettings,
} from '@/features/ai-credits/model/types';
import type {
  PassbookFlagKey,
  PassbookPermissionRow,
  StorePermissionRow,
} from '@/features/permissions/api/use-permissions';

interface MockResult {
  status: number;
  data: unknown;
}

interface MockDbSlice {
  passbooks: Record<string, Passbook>;
  clients: Array<{
    _id: string;
    name: string;
    inactive?: boolean;
    passbooks?: Array<{ _id: string }>;
  }>;
  stores: Record<
    string,
    Array<{ _id: string; storeCod: string; storeName: string; id_passbook: string }>
  >;
}

interface SessionUser {
  _id?: string;
  email?: string;
  name?: string;
  role?: string;
  id_passbooks?: string;
}

const MASTER_EMAIL = 'admin@epossible.com.br';
const DEFAULT_MODEL = 'openai/gpt-5.6-luna';
const FIELDS: AiSettingField[] = [
  'model',
  'monthlyLimitUsd',
  'renewMonthly',
];
/** Módulos do passbook que não são permissão de página do usuário. */
const PASSBOOK_ONLY_FLAG_KEYS: PassbookFlagKey[] = ['hasEmailMarketing', 'hasSmsMarketing'];

const FLAG_KEYS: PassbookFlagKey[] = [
  'hasCampaign',
  'hasCampaignAdvanced',
  'hasCampaignMetabase',
  'hasSimpleCampaignWhatsApp',
  'hasLoyaltyCampaigns',
  'hasProducts',
  'hasInfluencerCard',
  'hasConfig',
  'hasMultiStore',
  'notShowUsers',
  'hasSegmentations',
  'hasAudit',
  'hasMemberGetMember',
  'hasTierSettings',
  'hasTierOverride',
  'hasDashboard',
  'hasLeads',
  'hasOperator',
  'hasBanners',
  'hasFabButtons',
];

const CREATABLE_ROLES = ['Admin', 'Client', 'Owner', 'Manager', 'Operator', 'Integration', 'Validator', 'Base'];

const ok = (data: unknown): MockResult => ({ status: 200, data: { data } });
const notFound = (): MockResult => ({
  status: 404,
  data: { message: 'Registro não encontrado (mock).' },
});
const forbidden = (): MockResult => ({
  status: 403,
  data: { message: 'Acesso restrito ao master (mock).' },
});
const invalid = (message: string): MockResult => ({ status: 422, data: { message } });

/* ──────────────────────────── catálogo OpenRouter ──────────────────────────── */

/** Preço em USD por milhão de tokens (entrada, saída), como no site do OpenRouter. */
const CATALOG: Array<
  [id: string, name: string, promptPerM: number, completionPerM: number, context: number]
> = [
  ['openai/gpt-5.6-luna', 'OpenAI: GPT-5.6 Luna', 1.25, 10, 400_000],
  ['openai/gpt-5.6-terra', 'OpenAI: GPT-5.6 Terra', 0.25, 2, 400_000],
  ['openai/gpt-5-mini', 'OpenAI: GPT-5 Mini', 0.25, 2, 400_000],
  ['openai/gpt-4.1-mini', 'OpenAI: GPT-4.1 Mini', 0.4, 1.6, 1_047_576],
  ['anthropic/claude-sonnet-4.5', 'Anthropic: Claude Sonnet 4.5', 3, 15, 1_000_000],
  ['anthropic/claude-haiku-4.5', 'Anthropic: Claude Haiku 4.5', 1, 5, 200_000],
  ['google/gemini-2.5-pro', 'Google: Gemini 2.5 Pro', 1.25, 10, 1_048_576],
  ['google/gemini-2.5-flash', 'Google: Gemini 2.5 Flash', 0.3, 2.5, 1_048_576],
  ['deepseek/deepseek-chat-v3.1', 'DeepSeek: DeepSeek V3.1', 0.2, 0.8, 163_840],
  ['meta-llama/llama-4-maverick', 'Meta: Llama 4 Maverick', 0.15, 0.6, 1_048_576],
  ['x-ai/grok-4-fast', 'xAI: Grok 4 Fast', 0.2, 0.5, 2_000_000],
  ['qwen/qwen3-235b-a22b', 'Qwen: Qwen3 235B A22B', 0.13, 0.6, 262_144],
];

const MODELS: AiModelOption[] = CATALOG.map(([id, name, prompt, completion, contextLength]) => ({
  id,
  name,
  promptPrice: prompt / 1e6,
  completionPrice: completion / 1e6,
  contextLength,
})).sort((a, b) => a.id.localeCompare(b.id));

const modelById = new Map(MODELS.map((model) => [model.id, model]));

/* ──────────────────────────── helpers de data e sorteio ──────────────────────────── */

function mulberry32(seed: number) {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd = mulberry32(20260929);
const between = (min: number, max: number) => min + rnd() * (max - min);
const intBetween = (min: number, max: number) => Math.floor(between(min, max + 1));

const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const nextMonthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 1);
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

function slug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function paging(query: URLSearchParams) {
  const limit = Math.min(100, Math.max(1, Number(query.get('limit')) || 25));
  const page = Math.max(0, Number(query.get('page')) || 0);
  return { limit, skip: page * limit };
}

function matches(term: string | null, ...values: Array<string | undefined>): boolean {
  const t = (term ?? '').trim().toLowerCase();
  return !t || values.some((v) => (v ?? '').toLowerCase().includes(t));
}

/* ──────────────────────────── estado ──────────────────────────── */

interface AiStore {
  id: string;
  storeCod: string;
  storeName: string;
  city: string;
  idPassbook: string;
  aiSettings: Partial<AiSettings>;
}

interface AiUser {
  id: string;
  name: string;
  email: string;
  role: string;
  idPassbook: string;
  storeCodes: string[];
  /** Espelha `validated !== false` do legado. */
  active: boolean;
  hasCampaignAdvanced: boolean;
  pageFlags?: Partial<Record<PassbookFlagKey, boolean>>;
  aiSettings: Partial<AiSettings>;
  /** "Colocar mais créditos": consumo anterior a esta data não conta. */
  resetAt: Date | null;
}

interface UsageLog {
  userId: string;
  idPassbook: string;
  chatSessionId: string;
  segmentationId: string | null;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  aborted: boolean;
  createdAt: Date;
}

interface AiState {
  global: AiGlobalSettings;
  passbookSettings: Map<string, Partial<AiSettings>>;
  stores: AiStore[];
  users: AiUser[];
  logs: UsageLog[];
  segmentationNames: Map<string, string>;
}

let state: AiState | null = null;

const FIRST_NAMES = [
  'Ana',
  'Bruno',
  'Carla',
  'Diego',
  'Elisa',
  'Felipe',
  'Gabriela',
  'Henrique',
  'Isabela',
  'João',
  'Larissa',
  'Marcos',
  'Natália',
  'Otávio',
  'Paula',
  'Rafael',
  'Sofia',
  'Tiago',
  'Vanessa',
  'Yuri',
];
const LAST_NAMES = [
  'Almeida',
  'Barbosa',
  'Costa',
  'Duarte',
  'Ferreira',
  'Gomes',
  'Lima',
  'Martins',
  'Nogueira',
  'Oliveira',
  'Pereira',
  'Ribeiro',
  'Santos',
  'Teixeira',
  'Vieira',
];
const CITIES = [
  'São Paulo / SP',
  'Campinas / SP',
  'Rio de Janeiro / RJ',
  'Belo Horizonte / MG',
  'Curitiba / PR',
  'Porto Alegre / RS',
  'Brasília / DF',
  'Salvador / BA',
  'Recife / PE',
  'Goiânia / GO',
];
const STORE_NAMES = [
  'Centro',
  'Shopping Norte',
  'Shopping Sul',
  'Aeroporto',
  'Jardins',
  'Batel',
  'Savassi',
  'Asa Sul',
  'Boa Viagem',
  'Moinhos',
];
const ROLE_CYCLE = [
  'Client',
  'Campaign',
  'Operator',
  'Client',
  'Manager',
  'Campaign',
  'Validator',
  'Client',
];
const SEGMENTATION_NAMES = [
  'Clientes inativos há 60 dias',
  'Aniversariantes do mês',
  'Top 10% em pontos',
  'Primeira compra nos últimos 30 dias',
  'Resgataram e não voltaram',
  'Clientes VIP sem visita',
  'Carteira instalada sem compra',
  'Compraram 3+ vezes no trimestre',
  'Mulheres 25–40 com saldo alto',
  'Quase completando o cartão',
];

function buildStores(db: MockDbSlice): AiStore[] {
  const stores: AiStore[] = [];
  Object.values(db.passbooks).forEach((passbook, index) => {
    const real = db.stores[passbook._id];
    if (real && real.length > 0) {
      real.forEach((store, k) =>
        stores.push({
          id: store._id,
          storeCod: store.storeCod,
          storeName: store.storeName,
          city: CITIES[k % CITIES.length],
          idPassbook: passbook._id,
          aiSettings: {},
        }),
      );
      return;
    }
    const count = [0, 3, 2, 6, 4, 0, 1, 2][index % 8];
    const prefix = slug(passbook.organizationName).slice(0, 4) || 'loja';
    for (let k = 0; k < count; k++) {
      stores.push({
        id: `${passbook._id}-ai-store-${k}`,
        storeCod: `${prefix}${String(k + 1).padStart(3, '0')}`,
        storeName: STORE_NAMES[(index + k) % STORE_NAMES.length],
        city: CITIES[(index * 3 + k) % CITIES.length],
        idPassbook: passbook._id,
        aiSettings: {},
      });
    }
  });
  return stores;
}

function buildUsers(db: MockDbSlice, stores: AiStore[]): AiUser[] {
  const users: AiUser[] = [
    {
      id: 'u_admin',
      name: 'Admin Fixe',
      email: MASTER_EMAIL,
      role: 'Admin',
      idPassbook: Object.keys(db.passbooks)[0],
      storeCodes: [],
      active: true,
      hasCampaignAdvanced: true,
      aiSettings: {},
      resetAt: null,
    },
  ];

  Object.values(db.passbooks).forEach((passbook, index) => {
    const pbStores = stores.filter((store) => store.idPassbook === passbook._id);
    const domain = `${slug(passbook.organizationName) || 'loja'}.com.br`;
    const count = 2 + (index % 5);
    for (let k = 0; k < count; k++) {
      const first = FIRST_NAMES[(index * 7 + k * 3) % FIRST_NAMES.length];
      const last = LAST_NAMES[(index * 5 + k) % LAST_NAMES.length];
      const role = ROLE_CYCLE[(index + k) % ROLE_CYCLE.length];
      const storeCodes =
        pbStores.length === 0 || role === 'Client'
          ? []
          : [pbStores[(k + index) % pbStores.length].storeCod].concat(
              k % 4 === 3 && pbStores.length > 1
                ? [pbStores[(k + index + 1) % pbStores.length].storeCod]
                : [],
            );
      users.push({
        id: `u_ai_${passbook._id}_${k}`,
        name: `${first} ${last}`,
        email: `${slug(first)}.${slug(last)}@${domain}`,
        role,
        idPassbook: passbook._id,
        storeCodes,
        active: (index + k) % 7 !== 5,
        hasCampaignAdvanced: role === 'Client' || role === 'Campaign',
        aiSettings: {},
        resetAt: null,
      });
    }
  });
  return users;
}

/** Contas de login do mock (data.ts → userFromEmail) também aparecem nas telas. */
function addLoginUsers(users: AiUser[], db: MockDbSlice) {
  const ids = Object.keys(db.passbooks);
  const teashop = ids.find((id) => /tea/i.test(db.passbooks[id].organizationName)) ?? ids[0];
  users.push({
    id: 'u_client_teashop',
    name: 'Gestor TeaShop',
    email: 'gestor@teashop.com.br',
    role: 'Client',
    idPassbook: teashop,
    storeCodes: [],
    active: true,
    hasCampaignAdvanced: true,
    aiSettings: {},
    resetAt: null,
  });
}

function seedOverrides(s: AiState, db: MockDbSlice) {
  const ids = Object.keys(db.passbooks);
  const withAdvanced = ids.filter((id) => db.passbooks[id].hasCampaignAdvanced);
  const [first, second, third] = withAdvanced.length >= 3 ? withAdvanced : ids;

  // Segmentações liberadas em três passbooks; um com limite maior, outro com modelo próprio sem renovação.
  for (const id of [first, second, third]) if (id) db.passbooks[id].hasSegmentations = true;
  s.passbookSettings.set(first, { monthlyLimitUsd: 10 });
  if (second)
    s.passbookSettings.set(second, { model: 'anthropic/claude-sonnet-4.5', renewMonthly: false });
  if (third) s.passbookSettings.set(third, { model: 'google/gemini-2.5-flash' });

  const firstStores = s.stores.filter((store) => store.idPassbook === first);
  if (firstStores[0]) firstStores[0].aiSettings = { monthlyLimitUsd: 20 };
  if (firstStores[3]) firstStores[3].aiSettings = { model: 'openai/gpt-5.6-terra' };

  const panelUsers = s.users.filter((user) => user.email !== MASTER_EMAIL);
  const userOverrides: Array<[index: number, settings: Partial<AiSettings>]> = [
    [1, { monthlyLimitUsd: 15 }],
    [4, { model: 'openai/gpt-5-mini' }],
    [7, { monthlyLimitUsd: 2, renewMonthly: false }],
  ];
  for (const [index, settings] of userOverrides) {
    if (panelUsers[index]) panelUsers[index].aiSettings = settings;
  }
  if (panelUsers[4]) panelUsers[4].pageFlags = { hasSegmentations: true, hasProducts: true };
  if (panelUsers[10]) panelUsers[10].pageFlags = { hasSegmentations: false };
}

/** Gera conversas até o consumo do mês atingir `targetUsd` (e um histórico nos meses anteriores). */
function seedUsage(s: AiState, now: Date) {
  let session = 0;
  let segmentation = 0;

  const addSession = (user: AiUser, model: string, day: Date) => {
    const pricing = modelById.get(model) ?? modelById.get(DEFAULT_MODEL)!;
    const chatSessionId = `mock-chat-${++session}`;
    const linked = rnd() < 0.65;
    const segmentationId = linked ? `mock-seg-${++segmentation}` : null;
    if (segmentationId)
      s.segmentationNames.set(
        segmentationId,
        SEGMENTATION_NAMES[(segmentation - 1) % SEGMENTATION_NAMES.length],
      );
    const calls = intBetween(2, 7);
    const aborted = rnd() < 0.06;
    let cost = 0;
    for (let c = 0; c < calls; c++) {
      // O agente manda o recorte da base de leads no prompt: entrada pesada, saída curta.
      const inputTokens = intBetween(15_000, 45_000);
      const outputTokens = intBetween(150, 1_600);
      const costUsd = round6(
        inputTokens * pricing.promptPrice + outputTokens * pricing.completionPrice,
      );
      cost += costUsd;
      s.logs.push({
        userId: user.id,
        idPassbook: user.idPassbook,
        chatSessionId,
        segmentationId,
        model: pricing.id,
        inputTokens,
        outputTokens,
        costUsd,
        aborted: aborted && c === calls - 1,
        createdAt: new Date(day.getTime() + c * 45_000),
      });
    }
    return cost;
  };

  const randomDayIn = (monthOffset: number) => {
    const base = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
    const lastDay =
      monthOffset === 0
        ? now.getDate()
        : new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    const day = new Date(
      base.getFullYear(),
      base.getMonth(),
      intBetween(1, Math.max(1, lastDay)),
      intBetween(8, 20),
      intBetween(0, 59),
    );
    return day > now ? new Date(now.getTime() - intBetween(5, 90) * 60_000) : day;
  };

  const active = s.users.filter((user, index) => user.email === MASTER_EMAIL || index % 2 === 0);
  active.forEach((user, index) => {
    const eff = effectiveFor(s, user, user.idPassbook);
    const limit = eff.values.monthlyLimitUsd;
    // Alguns perto do limite e alguns bloqueados, para os KPIs terem o que mostrar.
    const fraction =
      user.email === MASTER_EMAIL
        ? 1.8
        : index % 9 === 4
          ? 1.05
          : index % 7 === 3
            ? 0.88
            : between(0.05, 0.5);
    const target = limit * fraction;
    let spent = 0;
    while (spent < target) spent += addSession(user, eff.values.model, randomDayIn(0));
    for (let month = 1; month <= 2; month++) {
      const sessions = intBetween(0, 4);
      for (let k = 0; k < sessions; k++) addSession(user, eff.values.model, randomDayIn(month));
    }
  });

  s.logs.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

function getState(db: MockDbSlice): AiState {
  if (state) return state;
  const stores = buildStores(db);
  const users = buildUsers(db, stores);
  addLoginUsers(users, db);
  state = {
    global: {
      model: DEFAULT_MODEL,
      monthlyLimitUsd: 5,
      renewMonthly: true,
    },
    passbookSettings: new Map(),
    stores,
    users,
    logs: [],
    segmentationNames: new Map(),
  };
  seedOverrides(state, db);
  seedUsage(state, new Date());
  return state;
}

/* ──────────────────────────── regras (espelho do legado) ──────────────────────────── */

const isSet = (v: unknown) => v !== null && v !== undefined;

function pickSettings(source?: Partial<AiSettings>): Partial<AiSettings> {
  const out: Partial<AiSettings> = {};
  for (const field of FIELDS) {
    if (source && isSet(source[field])) (out as Record<string, unknown>)[field] = source[field];
  }
  return out;
}

function combineStores(stores: AiStore[]): Partial<AiSettings> {
  const combined: Partial<AiSettings> = {};
  const defined = <K extends AiSettingField>(field: K) =>
    stores.map((store) => store.aiSettings[field]).filter(isSet) as Array<
      NonNullable<AiSettings[K]>
    >;
  const models = defined('model');
  if (models.length) combined.model = models[0];
  const limits = defined('monthlyLimitUsd');
  if (limits.length) combined.monthlyLimitUsd = Math.min(...limits);
  const renew = defined('renewMonthly');
  if (renew.length) combined.renewMonthly = renew.every(Boolean);
  return combined;
}

function resolve(layers: Array<[AiSettingSource, Partial<AiSettings>]>): EffectiveAiSettings {
  const values = {} as Record<AiSettingField, unknown>;
  const sources = {} as Record<AiSettingField, AiSettingSource>;
  for (const field of FIELDS) {
    for (const [source, layer] of layers) {
      if (isSet(layer[field])) {
        values[field] = layer[field];
        sources[field] = source;
        break;
      }
    }
  }
  return { values: values as unknown as AiGlobalSettings, sources };
}

function userStores(s: AiState, user: AiUser, idPassbook: string): AiStore[] {
  return s.stores.filter(
    (store) => store.idPassbook === idPassbook && user.storeCodes.includes(store.storeCod),
  );
}

function effectiveFor(s: AiState, user: AiUser, idPassbook: string): EffectiveAiSettings {
  return resolve([
    ['user', pickSettings(user.aiSettings)],
    ['store', combineStores(userStores(s, user, idPassbook))],
    ['passbook', pickSettings(s.passbookSettings.get(idPassbook))],
    ['global', s.global],
  ]);
}

function effectiveForStore(s: AiState, store: AiStore): EffectiveAiSettings {
  return resolve([
    ['store', pickSettings(store.aiSettings)],
    ['passbook', pickSettings(s.passbookSettings.get(store.idPassbook))],
    ['global', s.global],
  ]);
}

function periodStart(user: AiUser, renewMonthly: boolean, now: Date): Date {
  const base = renewMonthly ? monthStart(now) : new Date(0);
  return user.resetAt && user.resetAt > base ? user.resetAt : base;
}

function usageOf(s: AiState, user: AiUser, renewMonthly: boolean, now: Date) {
  const from = periodStart(user, renewMonthly, now);
  let usedUsd = 0;
  let input = 0;
  let output = 0;
  let lastUsedAt: Date | null = null;
  for (const log of s.logs) {
    if (log.userId !== user.id) continue;
    if (!lastUsedAt || log.createdAt > lastUsedAt) lastUsedAt = log.createdAt;
    if (log.createdAt < from) continue;
    usedUsd += log.costUsd;
    input += log.inputTokens;
    output += log.outputTokens;
  }
  return { usedUsd, input, output, lastUsedAt };
}

function summarize(limitUsd: number, usedUsd: number, unlimited: boolean) {
  const limit = Math.max(0, limitUsd);
  const remainingUsd = Math.max(0, limit - usedUsd);
  return {
    limitUsd: limit,
    usedUsd: round6(usedUsd),
    remainingUsd: round6(remainingUsd),
    percentUsed: limit > 0 ? Math.min(100, Math.round((usedUsd / limit) * 100)) : 100,
    exhausted: !unlimited && remainingUsd <= 0,
  };
}

function toUserRow(
  s: AiState,
  db: MockDbSlice,
  user: AiUser,
  idPassbook = user.idPassbook,
  now = new Date(),
): AiUserRow {
  const effective = effectiveFor(s, user, idPassbook);
  const usage = usageOf(s, user, effective.values.renewMonthly, now);
  const passbook = db.passbooks[idPassbook];
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    active: user.active,
    hasCampaignAdvanced: user.hasCampaignAdvanced,
    pageFlags: { ...user.pageFlags },
    passbook: passbook ? { id: passbook._id, name: passbook.organizationName } : null,
    stores: userStores(s, user, idPassbook).map((store) => ({
      code: store.storeCod,
      name: store.storeName,
    })),
    storeCodes: user.storeCodes,
    aiSettings: {
      model: null,
      monthlyLimitUsd: null,
      renewMonthly: null,
      ...pickSettings(user.aiSettings),
    },
    effective,
    credits: summarize(
      effective.values.monthlyLimitUsd,
      usage.usedUsd,
      user.email === MASTER_EMAIL,
    ),
    tokens: { input: usage.input, output: usage.output },
    renewsAt: effective.values.renewMonthly ? nextMonthStart(now).toISOString() : null,
    lastUsedAt: usage.lastUsedAt ? usage.lastUsedAt.toISOString() : null,
  };
}

function fullSettings(partial?: Partial<AiSettings>): AiSettings {
  return {
    model: null,
    monthlyLimitUsd: null,
    renewMonthly: null,
    ...pickSettings(partial),
  };
}

function passbookRow(s: AiState, db: MockDbSlice, passbook: Passbook): PassbookPermissionRow {
  const client = db.clients.find((c) => c.passbooks?.some((p) => p._id === passbook._id));
  const flags = {} as Record<PassbookFlagKey, boolean>;
  for (const key of [...FLAG_KEYS, ...PASSBOOK_ONLY_FLAG_KEYS])
    flags[key] = Boolean((passbook as unknown as Record<string, unknown>)[key]);
  return {
    id: passbook._id,
    name: passbook.organizationName,
    clientId: client?._id ?? '',
    clientName: client?.name ?? '',
    backgroundColor: passbook.backgroundColor,
    inactive: Boolean(client?.inactive),
    hasTiers: Boolean(passbook.hasTiers),
    flags,
    aiSettings: fullSettings(s.passbookSettings.get(passbook._id)),
    storeCount: s.stores.filter((store) => store.idPassbook === passbook._id).length,
  };
}

function storeRow(s: AiState, store: AiStore): StorePermissionRow {
  return {
    id: store.id,
    storeCode: store.storeCod,
    name: store.storeName,
    city: store.city,
    aiSettings: fullSettings(store.aiSettings),
    effective: effectiveForStore(s, store),
  };
}

/** Mesmo sanitize do legado: só chaves presentes; `null`/'' = volta a herdar. */
function applyPatch(target: Partial<AiSettings>, input: unknown): string | null {
  if (!input || typeof input !== 'object') return null;
  const patch = input as Record<string, unknown>;
  for (const field of FIELDS) {
    if (!(field in patch)) continue;
    const value = patch[field];
    if (value === null || value === undefined || value === '') {
      delete target[field];
      continue;
    }
    if (field === 'model') {
      if (!/^[a-z0-9][\w.-]*\/[\w.:-]+$/i.test(String(value)))
        return `Modelo inválido: ${String(value)}`;
      target.model = String(value);
    } else if (field === 'monthlyLimitUsd') {
      const limit = Number(value);
      if (!Number.isFinite(limit) || limit < 0)
        return 'Limite mensal deve ser um número maior ou igual a zero.';
      target.monthlyLimitUsd = Math.round(limit * 100) / 100;
    } else {
      if (typeof value !== 'boolean') return `${field} deve ser booleano.`;
      target[field] = value;
    }
  }
  return null;
}

/* ──────────────────────────── custos: agregações ──────────────────────────── */

function monthRange(month: string | null) {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? '');
  const base = match ? new Date(Number(match[1]), Number(match[2]) - 1, 1) : new Date();
  return { from: monthStart(base), to: nextMonthStart(base) };
}

function overview(s: AiState, db: MockDbSlice, month: string | null) {
  const { from, to } = monthRange(month);
  const logs = s.logs.filter((log) => log.createdAt >= from && log.createdAt < to);
  const sum = <K extends 'costUsd' | 'inputTokens' | 'outputTokens'>(key: K) =>
    logs.reduce((acc, log) => acc + log[key], 0);
  const userIds = new Set(logs.map((log) => log.userId));
  const rows = s.users.filter((user) => userIds.has(user.id)).map((user) => toUserRow(s, db, user));

  const group = (key: 'model' | 'idPassbook') => {
    const map = new Map<
      string,
      { costUsd: number; calls: number; sessions: Set<string>; segmentations: Set<string> }
    >();
    for (const log of logs) {
      const entry = map.get(log[key]) ?? {
        costUsd: 0,
        calls: 0,
        sessions: new Set(),
        segmentations: new Set(),
      };
      entry.costUsd += log.costUsd;
      entry.calls += 1;
      entry.sessions.add(log.chatSessionId);
      if (log.segmentationId) entry.segmentations.add(log.segmentationId);
      map.set(log[key], entry);
    }
    return [...map.entries()]
      .sort((a, b) => b[1].costUsd - a[1].costUsd)
      .map(
        ([id, v]) =>
          [
            id,
            {
              costUsd: round6(v.costUsd),
              calls: v.calls,
              sessions: v.sessions.size,
              segmentations: v.segmentations.size,
            },
          ] as const,
      );
  };

  return {
    month: `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}`,
    totals: {
      costUsd: round6(sum('costUsd')),
      inputTokens: sum('inputTokens'),
      outputTokens: sum('outputTokens'),
      calls: logs.length,
      sessions: new Set(logs.map((log) => log.chatSessionId)).size,
      segmentations: new Set(logs.map((log) => log.segmentationId).filter(Boolean)).size,
      activeUsers: userIds.size,
      nearLimitUsers: rows.filter((row) => !row.credits.exhausted && row.credits.percentUsed >= 80)
        .length,
      blockedUsers: rows.filter((row) => row.credits.exhausted).length,
    },
    byModel: group('model').map(([model, v]) => ({ model, ...v })),
    byPassbook: group('idPassbook')
      .slice(0, 10)
      .map(([idPassbook, v]) => ({
        idPassbook,
        name: db.passbooks[idPassbook]?.organizationName ?? idPassbook,
        ...v,
      })),
  };
}

type BreakdownTotals = { costUsd: number; calls: number; sessions: number; segmentations: number };

function summarizeLogs(logs: UsageLog[]): BreakdownTotals {
  return {
    costUsd: round6(logs.reduce((acc, log) => acc + log.costUsd, 0)),
    calls: logs.length,
    sessions: new Set(logs.map((log) => log.chatSessionId)).size,
    segmentations: new Set(logs.map((log) => log.segmentationId).filter(Boolean)).size,
  };
}

const BREAKDOWN_KEYS = { model: 'model', passbook: 'idPassbook', user: 'userId' } as const;
const BREAKDOWN_SORTS = ['costUsd', 'segmentations', 'sessions', 'costPerSegmentation'] as const;

function breakdown(s: AiState, db: MockDbSlice, query: URLSearchParams) {
  const { from, to } = monthRange(query.get('month'));
  const groupBy = (query.get('groupBy') ?? 'model') as keyof typeof BREAKDOWN_KEYS;
  const key = BREAKDOWN_KEYS[groupBy] ?? 'model';
  const sortParam = query.get('sort') as (typeof BREAKDOWN_SORTS)[number] | null;
  const sort = sortParam && BREAKDOWN_SORTS.includes(sortParam) ? sortParam : 'costUsd';
  const model = query.get('model');
  const idPassbook = query.get('idPassbook');
  const segmentation = query.get('segmentation');
  const search = query.get('search');

  const logs = s.logs.filter(
    (log) =>
      log.createdAt >= from &&
      log.createdAt < to &&
      (!model || log.model === model) &&
      (!idPassbook || log.idPassbook === idPassbook) &&
      (segmentation !== 'with' || Boolean(log.segmentationId)) &&
      (segmentation !== 'without' || !log.segmentationId),
  );

  const grouped = new Map<string, UsageLog[]>();
  for (const log of logs) grouped.set(log[key], [...(grouped.get(log[key]) ?? []), log]);

  const label = (id: string): { label: string; sublabel?: string } => {
    if (key === 'idPassbook') return { label: db.passbooks[id]?.organizationName ?? id };
    if (key === 'userId') {
      const user = s.users.find((u) => u.id === id);
      return user ? { label: user.name, sublabel: user.email } : { label: id };
    }
    return { label: id };
  };
  const sortValue = (row: BreakdownTotals) =>
    sort === 'costPerSegmentation'
      ? row.segmentations > 0
        ? row.costUsd / row.segmentations
        : -1
      : row[sort];

  const rows = [...grouped.entries()]
    .map(([id, list]) => ({ key: id, ...label(id), ...summarizeLogs(list) }))
    .filter((row) => matches(search, row.key, row.label, row.sublabel ?? ''))
    .sort((a, b) => sortValue(b) - sortValue(a));

  const summary = search
    ? summarizeLogs(logs.filter((log) => rows.some((row) => row.key === log[key])))
    : summarizeLogs(logs);
  const { limit, skip } = paging(query);
  return { rows: rows.slice(skip, skip + limit), total: rows.length, summary };
}

function usageRows(s: AiState, db: MockDbSlice, query: URLSearchParams) {
  const userId = query.get('userId');
  const segmentationId = query.get('segmentationId');
  const idPassbook = query.get('idPassbook');
  const sessions = new Map<string, UsageLog[]>();
  for (const log of s.logs) {
    if (userId && log.userId !== userId) continue;
    if (segmentationId && log.segmentationId !== segmentationId) continue;
    if (idPassbook && log.idPassbook !== idPassbook) continue;
    const list = sessions.get(log.chatSessionId) ?? [];
    list.push(log);
    sessions.set(log.chatSessionId, list);
  }

  const rows = [...sessions.entries()].map(([id, logs]) => {
    const firstLog = logs[0];
    const lastLog = logs[logs.length - 1];
    const user = s.users.find((u) => u.id === firstLog.userId);
    return {
      id,
      user: { id: firstLog.userId, name: user?.name ?? firstLog.userId },
      passbook: {
        id: firstLog.idPassbook,
        name: db.passbooks[firstLog.idPassbook]?.organizationName ?? firstLog.idPassbook,
      },
      segmentation: firstLog.segmentationId
        ? {
            id: firstLog.segmentationId,
            name: s.segmentationNames.get(firstLog.segmentationId) ?? 'Segmentação removida',
          }
        : null,
      model: lastLog.model,
      costUsd: round6(logs.reduce((acc, log) => acc + log.costUsd, 0)),
      inputTokens: logs.reduce((acc, log) => acc + log.inputTokens, 0),
      outputTokens: logs.reduce((acc, log) => acc + log.outputTokens, 0),
      calls: logs.length,
      aborted: logs.some((log) => log.aborted),
      startedAt: firstLog.createdAt.toISOString(),
      endedAt: lastLog.createdAt.toISOString(),
    };
  });
  rows.sort((a, b) => b.endedAt.localeCompare(a.endedAt));
  const { limit, skip } = paging(query);
  return { rows: rows.slice(skip, skip + limit), total: rows.length };
}

/* ──────────────────────────── rotas ──────────────────────────── */

const isMaster = (user: SessionUser | null) => user?.email?.trim().toLowerCase() === MASTER_EMAIL;

/** `GET /bff/ai/models` — como o BFF, só a família GPT-5.6 (o resto fica no catálogo só pro histórico de uso). */
export function handleAiModels(session: SessionUser | null): MockResult {
  if (!isMaster(session)) return forbidden();
  const models = MODELS.filter((model) => model.id.startsWith('openai/gpt-5.6'));
  return { status: 200, data: { data: { defaultModel: DEFAULT_MODEL, models } } };
}

/**
 * Rotas do legado sob `/bff/api/*`: `admin/permissions/*`, `admin/ai-credits/*`
 * e `ai-credits/me`. Devolve `null` quando a rota não é deste módulo.
 */
export function handleAiAdmin(
  method: string,
  a: string[],
  body: Record<string, unknown>,
  query: URLSearchParams,
  session: SessionUser | null,
  db: MockDbSlice,
): MockResult | null {
  const isAiMe = a[0] === 'ai-credits' && a[1] === 'me';
  const isAdmin = a[0] === 'admin' && (a[1] === 'permissions' || a[1] === 'ai-credits');
  if (!isAiMe && !isAdmin) return null;

  const s = getState(db);

  if (isAiMe && method === 'GET') {
    const idPassbook = query.get('idPassbook') ?? '';
    const passbook = db.passbooks[idPassbook];
    if (!passbook) return { status: 400, data: { message: 'idPassbook é obrigatório.' } };
    const user =
      s.users.find((u) => u.id === session?._id || u.email === session?.email) ??
      ({
        id: session?._id ?? 'u_mock',
        name: session?.name ?? 'Usuário',
        email: session?.email ?? '',
        role: session?.role ?? 'Client',
        idPassbook,
        storeCodes: [],
        active: true,
        hasCampaignAdvanced: false,
        aiSettings: {},
        resetAt: null,
      } satisfies AiUser);
    const row = toUserRow(s, db, user, idPassbook);
    return ok({
      segmentationsAllowed:
        isMaster(session) || (user.pageFlags?.hasSegmentations ?? Boolean(passbook.hasSegmentations)),
      percentUsed: row.credits.percentUsed,
      exhausted: row.credits.exhausted,
      renewMonthly: row.effective.values.renewMonthly,
      renewsAt: row.renewsAt,
    });
  }

  if (!isMaster(session)) return forbidden();
  const [, area, resource, id, sub] = a;

  /* ----- Permissões ----- */
  if (area === 'permissions') {
    if (resource === 'passbooks' && !id && method === 'GET') {
      const search = query.get('search');
      const all = Object.values(db.passbooks)
        .map((passbook) => passbookRow(s, db, passbook))
        .filter((row) => matches(search, row.name, row.clientName))
        .sort((x, y) => Number(x.inactive) - Number(y.inactive) || x.name.localeCompare(y.name));
      const { limit, skip } = paging(query);
      return ok({ rows: all.slice(skip, skip + limit), total: all.length });
    }

    if (resource === 'passbooks' && id) {
      const passbook = db.passbooks[id];
      if (!passbook) return notFound();

      if (!sub && method === 'GET') return ok(passbookRow(s, db, passbook));

      if (!sub && method === 'PUT') {
        const flags = (body.flags ?? {}) as Record<string, unknown>;
        for (const key of [...FLAG_KEYS, ...PASSBOOK_ONLY_FLAG_KEYS]) {
          if (typeof flags[key] === 'boolean')
            (passbook as unknown as Record<string, unknown>)[key] = flags[key];
        }
        const settings = { ...(s.passbookSettings.get(id) ?? {}) };
        const error = applyPatch(settings, body.aiSettings);
        if (error) return invalid(error);
        s.passbookSettings.set(id, settings);
        return ok(passbookRow(s, db, passbook));
      }

      if (sub === 'stores' && method === 'GET') {
        const search = query.get('search');
        const rows = s.stores
          .filter(
            (store) =>
              store.idPassbook === id &&
              matches(search, store.storeName, store.city, store.storeCod),
          )
          .sort((x, y) => x.storeName.localeCompare(y.storeName))
          .map((store) => storeRow(s, store));
        const { limit, skip } = paging(query);
        return ok({ rows: rows.slice(skip, skip + limit), total: rows.length });
      }

      if (sub === 'users' && method === 'POST') {
        const name = String(body.name ?? '').trim();
        const email = String(body.email ?? '').trim().toLowerCase();
        const password = String(body.password ?? '');
        const role = String(body.role ?? '');
        const storeCodes = Array.isArray(body.stores)
          ? [...new Set(body.stores.map((code) => String(code).trim()).filter(Boolean))]
          : [];
        if (!name) return invalid('Digite o nome.');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return invalid('O e-mail é inválido.');
        if (password.length < 6) return invalid('A senha precisa de ao menos 6 caracteres.');
        if (password.startsWith('_')) return invalid('A senha não pode começar com _.');
        if (!CREATABLE_ROLES.includes(role)) return invalid('Selecione um perfil válido.');
        if (s.users.some((user) => user.idPassbook === id && user.email.toLowerCase() === email)) {
          return invalid('O e-mail já está sendo utilizado neste passbook.');
        }
        const user: AiUser = {
          id: `u_${Date.now()}`,
          name,
          email,
          role,
          idPassbook: id,
          storeCodes,
          active: true,
          hasCampaignAdvanced: false,
          aiSettings: {},
          resetAt: null,
        };
        s.users.push(user);
        return ok(toUserRow(s, db, user, id));
      }

      if (sub === 'users' && method === 'GET') {
        const search = query.get('search');
        const storeCode = query.get('storeCode');
        const role = query.get('role');
        const status = query.get('status');
        const base = s.users.filter(
          (user) => user.idPassbook === id && user.email !== MASTER_EMAIL,
        );
        const rows = base
          .filter((user) => !storeCode || user.storeCodes.includes(storeCode))
          .filter((user) => !role || user.role === role)
          .filter((user) => status !== 'active' || user.active)
          .filter((user) => status !== 'inactive' || !user.active)
          .filter((user) => matches(search, user.name, user.email))
          .sort((x, y) => x.name.localeCompare(y.name))
          .map((user) => toUserRow(s, db, user, id));
        const inactive = base.filter((user) => !user.active).length;
        const { limit, skip } = paging(query);
        const roles = [...new Set(base.map((user) => user.role).filter(Boolean))].sort((a, b) =>
          a.localeCompare(b),
        );
        return ok({
          rows: rows.slice(skip, skip + limit),
          total: rows.length,
          roles,
          summary: {
            total: base.length,
            active: base.length - inactive,
            inactive,
            customPages: base.filter((user) => Object.keys(user.pageFlags ?? {}).length > 0).length,
          },
        });
      }
    }

    if (resource === 'stores' && id && method === 'PUT') {
      const store = s.stores.find((st) => st.id === id);
      if (!store) return notFound();
      const next = { ...store.aiSettings };
      const error = applyPatch(next, body.aiSettings);
      if (error) return invalid(error);
      store.aiSettings = next;
      return ok(storeRow(s, store));
    }

    if (resource === 'users' && id && method === 'GET') {
      const user = s.users.find((u) => u.id === id);
      if (!user) return notFound();
      return ok(toUserRow(s, db, user, query.get('idPassbook') ?? user.idPassbook));
    }

    if (resource === 'users' && id && method === 'PUT') {
      const user = s.users.find((u) => u.id === id);
      if (!user) return notFound();
      if (body.active === false && user.email === MASTER_EMAIL) {
        return invalid('O usuário master não pode ser desativado.');
      }
      const next = { ...user.aiSettings };
      const error = applyPatch(next, body.aiSettings);
      if (error) return invalid(error);
      user.aiSettings = next;
      if (typeof body.hasCampaignAdvanced === 'boolean')
        user.hasCampaignAdvanced = body.hasCampaignAdvanced;
      if (body.pageFlags && typeof body.pageFlags === 'object') {
        const patch = body.pageFlags as Record<string, unknown>;
        const next = { ...user.pageFlags };
        for (const key of FLAG_KEYS) {
          if (typeof patch[key] === 'boolean') next[key] = patch[key];
          else if (patch[key] === null) delete next[key];
        }
        user.pageFlags = next;
      }
      if (typeof body.name === 'string' || typeof body.email === 'string' || typeof body.role === 'string' || Array.isArray(body.stores) || (typeof body.password === 'string' && body.password.length > 0)) {
        if (user.email === MASTER_EMAIL) return invalid('O usuário master não pode ser editado por aqui.');
        if (typeof body.name === 'string') {
          const name = body.name.trim();
          if (!name) return invalid('Digite o nome.');
          user.name = name;
        }
        if (typeof body.email === 'string') {
          const email = body.email.trim().toLowerCase();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return invalid('O e-mail é inválido.');
          const idPassbook = typeof body.idPassbook === 'string' ? body.idPassbook : user.idPassbook;
          if (
            email !== user.email.toLowerCase() &&
            s.users.some((other) => other.id !== user.id && other.idPassbook === idPassbook && other.email.toLowerCase() === email)
          ) {
            return invalid('O e-mail já está sendo utilizado neste passbook.');
          }
          user.email = email;
        }
        if (typeof body.password === 'string' && body.password.length > 0) {
          if (body.password.length < 6) return invalid('A senha precisa de ao menos 6 caracteres.');
          if (body.password.startsWith('_')) return invalid('A senha não pode começar com _.');
        }
        if (typeof body.role === 'string' && body.role !== user.role && !CREATABLE_ROLES.includes(body.role)) {
          return invalid('Selecione um perfil válido.');
        }
        if (typeof body.role === 'string') user.role = body.role;
        if (Array.isArray(body.stores)) {
          user.storeCodes = [...new Set(body.stores.map((code) => String(code).trim()).filter(Boolean))];
        }
      }
      if (typeof body.active === 'boolean') user.active = body.active;
      return ok(
        toUserRow(
          s,
          db,
          user,
          typeof body.idPassbook === 'string' ? body.idPassbook : user.idPassbook,
        ),
      );
    }

    return notFound();
  }

  /* ----- Custos de IA ----- */
  if (resource === 'global' && method === 'GET') return ok(s.global);

  if (resource === 'global' && method === 'PUT') {
    const next: Partial<AiSettings> = { ...s.global };
    const error = applyPatch(next, body);
    if (error) return invalid(error);
    const missing = FIELDS.find((field) => !isSet(next[field]));
    if (missing) return invalid(`O padrão global precisa de ${missing}.`);
    s.global = next as AiGlobalSettings;
    let cleared = 0;
    if (body.clearUserLimits === true) {
      for (const user of s.users) {
        if (isSet(user.aiSettings.monthlyLimitUsd)) {
          delete user.aiSettings.monthlyLimitUsd;
          cleared++;
        }
      }
    }
    return ok({ settings: s.global, clearedUserLimits: cleared });
  }

  if (resource === 'overview' && method === 'GET') return ok(overview(s, db, query.get('month')));

  if (resource === 'breakdown' && method === 'GET') return ok(breakdown(s, db, query));

  if (resource === 'usage' && method === 'GET') return ok(usageRows(s, db, query));

  if (resource === 'users' && !id && method === 'GET') {
    const search = query.get('search');
    const idPassbook = query.get('idPassbook');
    const withActivity = new Set(s.logs.map((log) => log.userId));
    const rows = s.users
      .filter((user) => withActivity.has(user.id) || isSet(user.aiSettings.monthlyLimitUsd))
      .filter((user) => !idPassbook || user.idPassbook === idPassbook)
      .filter((user) => matches(search, user.name, user.email))
      .map((user) => toUserRow(s, db, user, idPassbook ?? user.idPassbook))
      .sort((x, y) => y.credits.usedUsd - x.credits.usedUsd);
    const { limit, skip } = paging(query);
    return ok({ rows: rows.slice(skip, skip + limit), total: rows.length });
  }

  if (resource === 'users' && id && sub === 'reset' && method === 'POST') {
    const user = s.users.find((u) => u.id === id);
    if (!user) return notFound();
    user.resetAt = new Date();
    return ok(toUserRow(s, db, user));
  }

  if (resource === 'bulk' && method === 'PUT') {
    const ids = new Set(Array.isArray(body.userIds) ? body.userIds.map(String) : []);
    let modified = 0;
    for (const user of s.users) {
      if (!ids.has(user.id)) continue;
      const error = applyPatch(user.aiSettings, { monthlyLimitUsd: body.monthlyLimitUsd });
      if (error) return invalid(error);
      modified++;
    }
    return ok({ modified });
  }

  return notFound();
}
